import React, { useState } from 'react';
import { Button } from './ui/button';
import { BookOpen, X, Eye } from 'lucide-react';

const SamplePreview = () => {
  const [showPreview, setShowPreview] = useState(false);

  const sampleExcerpt = `# The Life of Margaret Williams
## A Personal Memoir

### Chapter 1: A Coastal Childhood

I grew up in a small fishing village on the Cornish coast, where the sound of seagulls and crashing waves was as familiar as my own heartbeat. My earliest memories are painted in shades of blue and gold—the endless sky, the glittering sea, and the warm sand beneath my feet.

Every morning, I would wake to the smell of salt air drifting through my bedroom window. My father, a fisherman like his father before him, would already be at sea, his small boat a speck on the horizon. My mother would be in the kitchen, humming softly as she prepared breakfast, her hands eternally moving, eternally creating.

Those simple mornings shaped my understanding of love—not through grand gestures, but through consistency, through showing up, through the quiet dedication of daily life.

### Chapter 2: The Girl Who Loved Books

The village library became my sanctuary. Mrs. Henderson, the librarian with silver hair and kind eyes, would save books for me, knowing my voracious appetite for stories. Through those pages, I traveled to worlds far beyond our small village. I walked through Victorian London, explored the Amazon rainforest, and danced in Parisian ballrooms.

Books taught me that the world was vast and full of possibility. They taught me that my small-town life was not a limitation but a foundation—a solid ground from which I could launch myself into any future I dared to imagine...`;

  return (
    <div className="py-20 px-4 bg-white">
      <div className="max-w-6xl mx-auto text-center">
        <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">
          See What Your Story Could Look Like
        </h2>
        <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
          Every autobiography is unique, beautifully written, and captures the essence of a life well-lived
        </p>

        <Button
          onClick={() => setShowPreview(true)}
          className="btn-primary text-white px-8 py-6 text-lg font-semibold rounded-full border-0 inline-flex items-center gap-2"
        >
          <Eye className="w-5 h-5" />
          Read Sample Autobiography
        </Button>

        {/* Modal */}
        {showPreview && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-3xl max-w-4xl w-full max-h-[90vh] overflow-hidden shadow-2xl">
              {/* Header */}
              <div className="bg-gradient-to-r from-pink-100 to-purple-100 p-6 flex justify-between items-center">
                <div className="flex items-center gap-3">
                  <BookOpen className="w-6 h-6 text-purple-600" />
                  <h3 className="text-2xl font-bold text-gray-800">Sample Autobiography</h3>
                </div>
                <button
                  onClick={() => setShowPreview(false)}
                  className="text-gray-600 hover:text-gray-800"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              {/* Content */}
              <div className="p-8 overflow-y-auto max-h-[calc(90vh-100px)]">
                <div className="book-page rounded-xl p-8 md:p-10">
                  <div className="prose prose-lg max-w-none">
                    <div className="whitespace-pre-wrap text-gray-700 leading-relaxed">
                      {sampleExcerpt.split('\n').map((line, index) => {
                        if (line.startsWith('###')) {
                          return (
                            <h3 key={index} className="text-2xl font-bold text-gray-800 mt-8 mb-4">
                              {line.replace('###', '').trim()}
                            </h3>
                          );
                        } else if (line.startsWith('##')) {
                          return (
                            <h2 key={index} className="text-3xl font-bold text-gray-800 mt-6 mb-3 text-center italic">
                              {line.replace('##', '').trim()}
                            </h2>
                          );
                        } else if (line.startsWith('#')) {
                          return (
                            <h1 key={index} className="text-4xl font-bold text-gray-800 mb-2 text-center">
                              {line.replace('#', '').trim()}
                            </h1>
                          );
                        } else if (line.trim() !== '') {
                          return (
                            <p key={index} className="mb-4 text-justify">
                              {line}
                            </p>
                          );
                        }
                        return <br key={index} />;
                      })}
                    </div>
                  </div>

                  <div className="mt-8 pt-6 border-t border-yellow-100">
                    <p className="text-center text-sm text-gray-600 italic">
                      This is just a glimpse. Your complete autobiography will include 6-8 chapters, 
                      3,000-6,000 words, and capture your entire life journey.
                    </p>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="bg-gray-50 p-6 text-center border-t">
                <p className="text-gray-700 mb-4">
                  Ready to create your own life story?
                </p>
                <Button
                  onClick={() => {
                    setShowPreview(false);
                    // Scroll to CTA or trigger start flow
                    document.querySelector('.btn-primary')?.scrollIntoView({ behavior: 'smooth' });
                  }}
                  className="btn-primary text-white px-8 py-4 text-lg rounded-full border-0"
                >
                  Start My Story
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SamplePreview;
